import { MinimalLayout } from '@/components/layouts/MinimalLayout'
import { Navbar } from '@/components/nav/Navbar'
import { OrgToolsTable } from '@/components/OrgToolsTable/OrgToolsTable'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  flattenToolPages,
  useCreateTool,
  useDeleteTool,
  useListToolsQuery,
  useUpdateTool,
} from '@/services/queries/tools'
import {
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useMemo, useState } from 'react'
import { useIsReadOnly } from '@/pages/CADPage/hooks/useIsReadOnly'

export const ToolsPage = () => {
  const [searchValue, setSearchValue] = useState('')
  const { data: readOnlyData } = useIsReadOnly()
  /**
   * Tools library permissions are user-scoped. We intentionally key off
   * `isUserReadOnly` so this route does not inherit document-version read-only.
   */
  const isReadOnly = Boolean(readOnlyData?.isUserReadOnly)
  const { data, isLoading, fetchNextPage } = useListToolsQuery({
    limit: 10,
    offset: 0,
    search: searchValue,
  })
  const tools = useMemo(() => flattenToolPages(data), [data])
  const totalTools = data?.pages?.[0]?.totalTools ?? 0
  const { mutateAsync: deleteTool } = useDeleteTool()
  const { mutateAsync: updateTool } = useUpdateTool()

  return (
    <MinimalLayout>
      <Navbar>
        <div className="flex items-center space-x-4 h-10 py-8 w-full" />
      </Navbar>
      <div className="flex items-center justify-between px-6 py-5">
        <div>
          <h1 className="text-xl font-semibold tracking-[-0.015em] text-zinc-950 mx-10">
            Tools Library
          </h1>
          <h2 className="mx-10 text-zinc-500 text-sm">
            Manage the tools available across all your projects and documents.
          </h2>
        </div>
        <div className="flex items-center gap-3 px-12">
          {!isReadOnly && <AddToolButton />}
        </div>
      </div>
      <div className="px-16">
        <div className="relative w-full max-w-sm">
          <Input
            type="text"
            placeholder="Search tools..."
            className="h-9 rounded-md border-zinc-300 bg-white pl-8 pr-8 text-sm"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            data-testid="tools-page-search-input"
          />
          <MagnifyingGlassIcon className="w-4 absolute left-2.5 top-2.5 text-zinc-400 pointer-events-none" />
          <button
            className="absolute right-2 top-2 rounded p-0.5 text-zinc-400 hover:bg-zinc-100"
            onClick={() => setSearchValue('')}
            data-testid="tools-page-clear-search-button"
          >
            <XMarkIcon className="w-4" />
          </button>
        </div>
      </div>
      <div className="py-6 px-16">
        <OrgToolsTable
          tools={tools}
          totalTools={totalTools}
          isLoading={isLoading}
          fetchNextPage={() => {
            void fetchNextPage()
          }}
          onUpdate={updateTool}
          onDelete={deleteTool}
          isReadOnly={isReadOnly}
        />
      </div>
    </MinimalLayout>
  )
}

const AddToolButton = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [name, setName] = useState('')

  const { mutate: createTool } = useCreateTool()

  const handleCreate = () => {
    createTool({ name })
    setName('')
    setIsOpen(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          className="gap-1 rounded-full"
          data-testid="tools-page-add-tool-button"
        >
          <PlusIcon className="h-4" />
          <span>Add Tool</span>
        </Button>
      </DialogTrigger>
      <DialogPortal>
        <DialogContent
          ariaLabel="Add new tool dialog"
          className="max-w-lg min-h-0"
          data-testid="tools-page-add-tool-dialog"
        >
          <DialogHeader>
            <DialogTitle>Add Tool</DialogTitle>
          </DialogHeader>
          <div className="flex flex-row gap-4 mt-8">
            <Input
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="tools-page-add-tool-name-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreate()
                }
              }}
            />
            <Button
              onClick={handleCreate}
              disabled={!name}
              data-testid="tools-page-add-tool-submit"
            >
              Add
            </Button>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
